/**
 * Les réglages du site, vus d'un écran React.
 *
 * ## Ce que rend ce crochet, et pourquoi `null` existe
 *
 * `null` veut dire « on ne sait pas encore ». Il dure le temps d'une requête,
 * et seulement à la toute première visite.
 *
 * La version précédente rendait les valeurs par défaut pendant ce temps-là, ce
 * qui évitait à chaque page d'avoir un état d'attente à gérer. Mauvais calcul :
 * les valeurs par défaut sont les anciennes photos, celles qui étaient en dur
 * avant la table des réglages. Chaque visiteur voyait donc l'ancienne image,
 * puis la nouvelle — un clignotement à chaque arrivée sur le site. Le repli
 * conçu pour le jour où la requête échoue se déclenchait en réalité à chaque
 * chargement normal.
 *
 * Désormais les deux cas sont distingués :
 *
 * - on ne sait pas encore → `null`, et l'écran laisse un cadre neutre ;
 * - la requête a échoué → les valeurs par défaut, comme avant : le site
 *   d'hier vaut mieux qu'un trou.
 *
 * ## Pourquoi la première visite est la seule concernée
 *
 * Le dernier jeu de réglages connu est retenu dans le navigateur. Quelqu'un
 * qui est déjà venu tient la bonne adresse dès le premier rendu, sans attendre
 * la requête — et le téléchargement de l'image commence tout de suite. La
 * requête part quand même, en arrière-plan : si l'équipe a changé une photo
 * entre-temps, l'écran se met à jour.
 */

import { useEffect, useState } from 'react';
import { PAR_DEFAUT, type Reglages } from './index';
import { lireReglagesOuLeve } from './gateway.supabase';
import { ecrireCache, lireCache, rangementDuNavigateur } from './cache';

export function useReglages(): Reglages | null {
  /*
    Lu dans l'initialiseur, donc avant le premier rendu : c'est ce qui evite le
    clignotement. Le lire dans un effet serait deja trop tard — le cadre neutre
    aurait eu le temps d'etre peint.
  */
  const [reglages, setReglages] = useState<Reglages | null>(() =>
    lireCache(rangementDuNavigateur()),
  );

  useEffect(() => {
    let annule = false;

    lireReglagesOuLeve().then(
      (r) => {
        if (annule) return;
        setReglages(r);
        ecrireCache(r, rangementDuNavigateur());
      },
      (err) => {
        console.error('Réglages du site non chargés :', err);
        /*
          Ce qu'on avait en cache vaut mieux que les valeurs par defaut : c'est
          l'etat du site a la derniere visite, pas celui d'avant la table. On ne
          retombe sur les defauts que si l'on ne sait vraiment rien.
        */
        if (!annule) setReglages((actuel) => actuel ?? PAR_DEFAUT);
      },
    );

    return () => {
      annule = true;
    };
  }, []);

  return reglages;
}
